import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementCluster,
  IElementTypeHint,
  isIElementCluster,
  isIElementSectionWithTypeHint,
  WithHint,
} from "@faro-lotv/ielement-types";
import { selectChildrenDepthFirst } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/** A cluster element with the siteScapeCapture type hint */
export type IElementSiteScapeCapture = WithHint<
  IElementCluster,
  IElementTypeHint.siteScapeCapture
>;

/**
 * @param iElement to check
 * @returns whether the given element is a siteScapeCapture cluster
 */
export function isIElementSiteScapeCapture(
  iElement: IElement,
): iElement is IElementSiteScapeCapture {
  return (
    isIElementCluster(iElement) &&
    iElement.typeHint === IElementTypeHint.siteScapeCapture
  );
}

/**
 * @returns A selector to get all siteScapeScan children of the given siteScapeCapture cluster
 * @param state - The iElements state
 * @param siteScapeCapture - The siteScapeCapture cluster element
 */
export const selectSiteScapeScanChildren = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, siteScapeCapture: IElement) =>
        siteScapeCapture,
    ],
    (iElements, siteScapeCapture): IElement[] => {
      if (!isIElementSiteScapeCapture(siteScapeCapture)) {
        return [];
      }

      return selectChildrenDepthFirst(siteScapeCapture, (el) =>
        isIElementSectionWithTypeHint(el, IElementTypeHint.siteScapeScan),
      )({
        iElements,
      });
    },
  ),
);

/**
 * @returns A selector that resolves siteScapeCapture cluster IDs into their actual siteScapeScan children IDs
 * @param state - The iElements state
 * @param selectedIds - The list of selected IDs to resolve
 * @param scans - A map of scan IDs to their IElement objects
 */
export const selectResolvedSiteScapeIds = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, selectedIds: GUID[]) => selectedIds,
      (
        state: OnlyIElementsStore,
        selectedIds: GUID[],
        scans: Map<GUID, IElement>,
      ) => scans,
    ],
    (iElements, selectedIds, scans): GUID[] =>
      selectedIds.flatMap((id) => {
        const scan = scans.get(id);
        if (!scan || !isIElementSiteScapeCapture(scan)) {
          return id;
        }
        return selectSiteScapeScanChildren(scan)({ iElements }).map(
          (child) => child.id,
        );
      }),
  ),
);
